/**
 * Build a real auto-route from the chart: lay a grid over the start-to-destination
 * area, work out which nodes are navigable for this draught, and give the A*
 * engine a cost that uses the tide (and, with a polar, the wind) at each step.
 *
 * Kept separate from the search so the cost-building is testable with synthetic
 * cells and injected tide/wind. Honest about its inputs (coarse grid, sparse
 * tide, coarse wind): a SUGGESTED route to check at high zoom, not an optimum.
 */
import type { ChartFeatureCollection } from '@/types';
import { courseToSteer, rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import { navigableAt, ChartIndex } from '@/safety';
import { autoRoute, simplify, stringPull, type AutoRouteResult, type GridSpec } from './autoRoute';
import { boatSpeedFromPolar, trueWindAngle, type BoatPolar } from './polar';

export interface PlanAutoRouteArgs {
  start: [number, number];
  destination: [number, number];
  departMs: number;
  cells: ChartFeatureCollection[];
  draughtM: number;
  /** Extra water-under-keel margin to keep, metres. */
  clearanceM: number;
  /** Motor cruise speed, used when there's no polar. */
  cruiseKn: number;
  /** Height of tide to add to charted depth for the navigability check. */
  tideHeightM?: number;
  /** A sailing polar: when present, the router sails (and tacks); when null, it motors. */
  polar?: BoatPolar | null;
  tideFn: (pos: [number, number], ms: number) => { setDeg: number; rateKn: number };
  windFn: (pos: [number, number], ms: number) => { dirFromDeg: number; speedKn: number };
  /** Approx grid spacing, nm (default 0.25). */
  spacingNm?: number;
}

export interface PlanAutoRouteResult extends AutoRouteResult {
  grid: GridSpec;
  /**
   * True when the route was plotted but comes with a limitation the crew must
   * check: the start or destination is over charted-shallow water, or the coarse
   * grid couldn't thread a clear channel so we fell back to a direct line. The
   * route is still returned (we plot and warn, we never silently refuse); the
   * warnings say what to look at, and the tide-aware Check route spells out the
   * gates.
   */
  limited?: boolean;
  /** Plain-English limitations to surface alongside the plotted route. */
  warnings?: string[];
}

export type PlanAutoRouteOutcome = PlanAutoRouteResult | { error: string };

export function planAutoRoute(args: PlanAutoRouteArgs): PlanAutoRouteOutcome {
  const { start, destination } = args;
  const spacingNm = args.spacingNm ?? 0.25;

  const padLon = Math.abs(destination[0] - start[0]) * 0.4 + 0.04;
  const padLat = Math.abs(destination[1] - start[1]) * 0.4 + 0.04;
  const minLon = Math.min(start[0], destination[0]) - padLon;
  const maxLon = Math.max(start[0], destination[0]) + padLon;
  const minLat = Math.min(start[1], destination[1]) - padLat;
  const maxLat = Math.max(start[1], destination[1]) + padLat;
  const midLat = (minLat + maxLat) / 2;
  const widthNm = (maxLon - minLon) * 60 * Math.cos((midLat * Math.PI) / 180);
  const heightNm = (maxLat - minLat) * 60;
  const cols = Math.max(8, Math.min(60, Math.round(widthNm / spacingNm) + 1));
  const rows = Math.max(8, Math.min(60, Math.round(heightNm / spacingNm) + 1));
  const grid: GridSpec = { minLon, minLat, maxLon, maxLat, cols, rows };

  const tideHeightM = args.tideHeightM ?? 0;
  const draught = args.draughtM;
  const clearance = args.clearanceM;
  const cruise = Math.max(0.5, args.cruiseKn);
  const polar = args.polar ?? null;

  // One spatial index over the cells, so each node's depth check is a box query,
  // not a full feature scan. Memoise navigability per node on top of that.
  const index = new ChartIndex(args.cells);
  const navCache = new Map<string, boolean>();
  const isNav = (p: [number, number]): boolean => {
    const k = `${p[0].toFixed(5)},${p[1].toFixed(5)}`;
    let v = navCache.get(k);
    if (v === undefined) {
      v = navigableAt(p, index, draught, clearance, tideHeightM);
      navCache.set(k, v);
    }
    return v;
  };

  // A point is LAND if even a generous tide can't float it (the depth check is
  // driven by charted soundings/areas; land polygons never float). We use this to
  // keep an endpoint's approach leg from cutting across a headland even when the
  // endpoint itself is charted-shallow water.
  const isLand = (p: [number, number]): boolean =>
    !navigableAt(p, index, draught, clearance, 1000);
  const stepNmApproach = Math.max(0.02, spacingNm / 2);
  // The straight approach from a candidate anchor node to the exact (shallow)
  // endpoint is acceptable as long as it doesn't cross land. It may pass over the
  // shallow around the endpoint — that's the warned bit — but never over land.
  const approachLandClear = (anchor: [number, number], endpoint: [number, number]): boolean => {
    const dist = rhumbDistanceNm(anchor, endpoint);
    const steps = Math.max(1, Math.ceil(dist / stepNmApproach));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (isLand([anchor[0] + (endpoint[0] - anchor[0]) * t, anchor[1] + (endpoint[1] - anchor[1]) * t])) return false;
    }
    return true;
  };

  // Is the straight run between two points navigable ALONG ITS LENGTH, not just at
  // its ends? The grid is coarse (nodes ~spacingNm apart), so a leg between two
  // navigable nodes can still clip a thin shoal or the corner of a landmass sitting
  // between them. Sampling at half the grid spacing catches that, so the router
  // avoids hazards on its edges and not only at its nodes. Used both to gate the
  // A* edges and to smooth the finished path, so the two can never disagree.
  const lineNavClear = (a: [number, number], b: [number, number]): boolean => {
    const dist = rhumbDistanceNm(a, b);
    const steps = Math.max(1, Math.ceil(dist / stepNmApproach));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (!isNav([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])) return false;
    }
    return true;
  };

  // We used to refuse here when the start or destination sat over charted-shallow
  // water. That was wrong on two counts: the tide sits on top of the charted
  // depth (so it may float fine), and refusing leaves the crew with nothing to
  // work from. So we plot the route regardless and carry the limitation forward
  // as a warning; the tide-aware Check route then works out the real gate.
  const warnings: string[] = [];
  if (!isNav(start)) warnings.push('The start is over water charted shallow for this draught (the tide may still float it).');
  if (!isNav(destination))
    warnings.push('The destination is over water charted shallow for this draught (the tide may still float it).');

  const edgeCost = (from: [number, number], to: [number, number], ms: number) => {
    // Block the edge unless the whole run between the two nodes stays navigable, so
    // a leg can't step over a thin shoal or nick a headland between grid nodes.
    if (!lineNavClear(from, to)) return null;
    const dist = rhumbDistanceNm(from, to);
    if (dist <= 0) return null;
    const bearing = rhumbBearingDeg(from, to);
    const mid: [number, number] = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
    const tide = args.tideFn(mid, ms);

    let boatSpeed = cruise;
    let penalty = 0;
    if (polar) {
      const wind = args.windFn(mid, ms);
      const twa = trueWindAngle(bearing, wind.dirFromDeg);
      boatSpeed = boatSpeedFromPolar(polar, twa, wind.speedKn);
      if (boatSpeed < 0.3) {
        // Too close to the wind to sail: keep the edge but penalise it so the
        // route tacks across other angles instead of pointing straight up-wind.
        boatSpeed = 0.3;
        penalty += dist * 4;
      }
    }

    const cts = courseToSteer({
      from,
      to,
      tidalSet: tide.setDeg,
      tidalRate: tide.rateKn,
      boatSpeed,
      leewayDeg: 0,
    });
    if (cts.unachievable || !Number.isFinite(cts.sogKnots) || cts.sogKnots <= 0) return null;
    return { timeH: dist / cts.sogKnots, penalty };
  };

  const result = autoRoute({
    grid,
    start,
    destination,
    departMs: args.departMs,
    edgeCost,
    heuristicSpeedKn: (polar ? 7 : cruise) + 3,
    // Anchor the search on the nearest genuinely navigable node when the chosen
    // start or destination is over charted-shallow water or on the coast. Without
    // this, an unnavigable endpoint node has no edge into it, A* returns null, and
    // we fall back to a straight line drawn through every hazard — which is the
    // regression this guards against. With it, the router still threads a clear
    // channel and only the short approach to the shallow endpoint is warned about.
    passable: isNav,
    // ...and that short approach must not cross land (a headland between the anchor
    // and a charted-shallow destination), so we pick the nearest navigable anchor
    // with a land-clear run to the exact endpoint.
    endpointApproachClear: approachLandClear,
  });
  if (!result) {
    // Even anchored on navigable water, the coarse grid found no channel between
    // start and destination (a wall of shallow/land across the area, or an endpoint
    // with no navigable water near it at all). Only NOW do we fall back to a direct
    // line — genuinely no route exists — and we say so plainly so the crew checks it
    // themselves rather than trusting a straight line through the hazards.
    warnings.push(
      'No clear route found — direct line shown, check it yourself. Run Check route for the shallow legs and tidal gates.',
    );
    const straight = simplify([start, destination]);
    const distNm = rhumbDistanceNm(start, destination);
    return {
      waypoints: straight,
      totalTimeH: distNm / cruise,
      expanded: 0,
      grid,
      limited: true,
      warnings,
    };
  }
  // Line-of-sight smoothing: collapse the staircase dog-legs the coarse
  // 8-direction grid leaves on a straight run. We only straighten a jog when the
  // direct segment stays navigable along its length (the same lineNavClear the A*
  // edges use, so smoothing can never straighten a jog across water the router
  // considers unsafe). A jog that's the only way round land/shallow is kept.
  const waypoints = stringPull(result.waypoints, lineNavClear);

  return {
    ...result,
    waypoints,
    grid,
    limited: warnings.length > 0 ? true : undefined,
    warnings: warnings.length ? warnings : undefined,
  };
}
